/**
 * In-flight GET request deduplication.
 *
 * Prevents duplicate network requests when multiple components (or React
 * StrictMode double-effects in dev) request the same URL simultaneously.
 * Identical concurrent GETs share a single Promise; once it resolves or
 * rejects, the entry is cleared so the next call hits the network normally.
 *
 * Usage:
 *   import { apiGet } from "@/lib/apiCache";
 *   const res = await apiGet(`${API}/products`);
 *
 * Not a response cache — after resolution the entry is removed. Backend
 * Cache-Control headers / SimpleCache still govern actual caching.
 */
import axios from "axios";

const inflight = new Map();

function keyFor(url, config) {
  // Include query params / headers that meaningfully change the response.
  const params = config?.params ? JSON.stringify(config.params) : "";
  const auth = config?.headers?.Authorization || "";
  return `${url}|${params}|${auth}`;
}

export function apiGet(url, config = {}) {
  const key = keyFor(url, config);
  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = axios.get(url, config).finally(() => {
    // Clear regardless of success/failure so next call is fresh.
    inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
}

// Escape hatch for tests / manual invalidation.
export function _clearInflight() {
  inflight.clear();
}
